'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { MoreOptionsButton } from './MoreOptionsButton';

/**
 * Finds the LiveKit control bar and appends an inline MoreOptionsButton inside it.
 * The button will be positioned between the Leave button and the Invite button.
 */
export function InjectMoreOptionsIntoBar() {
  const [target, setTarget] = React.useState<HTMLElement | null>(null);
  const placeholderRef = React.useRef<HTMLSpanElement | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const ensurePlaceholder = () => {
      if (!placeholderRef.current) {
        const el = document.createElement('span');
        el.style.display = 'inline-flex';
        el.style.position = 'relative';
        el.style.overflow = 'visible';
        el.setAttribute('data-more-options-injected', 'true');
        placeholderRef.current = el;
      }
      return placeholderRef.current!;
    };

    const tryInsertAtAnchor = () => {
      const bar = document.querySelector('.lk-control-bar') as HTMLElement | null;
      if (!bar) return false;

      const leaveBtn = bar.querySelector(':scope > .lk-disconnect-button') as HTMLElement | null;
      if (!leaveBtn || !leaveBtn.parentElement) return false;

      const placeholder = ensurePlaceholder();
      if (!placeholder.isConnected) {
        leaveBtn.insertAdjacentElement('afterend', placeholder);
      }
      setTarget(placeholder);
      return true;
    };

    if (!tryInsertAtAnchor()) {
      const observer = new MutationObserver(() => {
        tryInsertAtAnchor();
      });
      observer.observe(document.body, { childList: true, subtree: true });

      return () => {
        observer.disconnect();

        const el = placeholderRef.current;
        if (el && el.parentElement) {
          el.parentElement.removeChild(el);
        }
      };
    }

    return () => {
      const el = placeholderRef.current;
      if (el && el.parentElement) {
        el.parentElement.removeChild(el);
      }
    };
  }, []);

  if (!target) return null;

  return createPortal(<MoreOptionsButton inline />, target);
}
